import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  IconButton,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material'
import {
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  AttachMoney as AttachMoneyIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Token as TokenIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import {
  billingService,
  TokenUsageSummary,
  TenantTokenUsageDetail,
} from '../services/billingService'
import { useAuth } from '../contexts/AuthContext'
import { format } from 'date-fns'
import { ja, enUS } from 'date-fns/locale'

// 型定義
interface BillingPlan {
  id: string
  planType: string
  name: string
  nameJa: string
  description: string
  basePrice: number
  pricePerUser: number
  maxUsers: number | null
  aiCreditsPerMonth: number | null
  aiCreditPrice: number
  creditCarryoverMonths: number
  reportRetentionYears: number
  maxLearningPlans: number | null
  features: string
  isActive: boolean
  sortOrder: number
}

interface Subscription {
  id: string
  tenantId: string
  billingPlanId: string
  status: 'ACTIVE' | 'CANCELLED' | 'SUSPENDED'
  startDate: string
  endDate: string | null
  billingEmail: string | null
  billingName: string
  tenant: {
    id: string
    name: string
    slug: string
    isActive: boolean
  }
  billingPlan: BillingPlan
  activeUserCount: number
  _count: {
    invoices: number
  }
}

interface Invoice {
  id: string
  subscriptionId: string
  tenantId: string
  invoiceNumber: string
  billingPeriodStart: string
  billingPeriodEnd: string
  baseAmount: number
  userAmount: number
  aiCreditAmount: number
  subtotal: number
  taxRate: number
  taxAmount: number
  totalAmount: number
  userCount: number
  activeUserCount: number
  aiCreditsUsed: number
  aiCreditsExtra: number
  status: 'DRAFT' | 'PENDING' | 'SENT' | 'PAID' | 'OVERDUE'
  issuedAt: string
  dueDate: string | null
  sentAt: string | null
  paidAt: string | null
  tenant: {
    id: string
    name: string
    slug: string
  }
  subscription: {
    billingPlan: {
      name: string
      nameJa: string
    }
  }
}

interface CreditInfo {
  tenant: {
    id: string
    name: string
    slug: string
  }
  plan: {
    name: string
    nameJa: string
    aiCreditsPerMonth: number | null
  }
  balance: {
    yearMonth: string
    creditsGranted: number
    creditsCarriedOver: number
    creditsUsed: number
    creditsExpired: number
    creditsRemaining: number
    creditsPurchased: number
  } | null
}

interface SchedulerStatus {
  isRunning: boolean
  lastExecution: string | null
  nextExecution: string | null
  isScheduled: boolean
  lastResult: any | null
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
)

const BillingManagementPage: React.FC = () => {
  const { i18n } = useTranslation()
  const locale = i18n.language === 'ja' ? ja : enUS
  const { token } = useAuth()

  // State
  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Data states
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [credits, setCredits] = useState<CreditInfo[]>([])
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null)

  // Token usage states
  const [tokenUsageSummary, setTokenUsageSummary] = useState<TokenUsageSummary | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [tokenUsageLoading, setTokenUsageLoading] = useState(false)
  const [tenantDetailDialogOpen, setTenantDetailDialogOpen] = useState(false)
  const [selectedTenantDetail, setSelectedTenantDetail] = useState<TenantTokenUsageDetail | null>(
    null
  )
  const [tenantDetailLoading, setTenantDetailLoading] = useState(false)

  // Dialog states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [newPlanId, setNewPlanId] = useState('')
  const [planChangeReason, setPlanChangeReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // プラン設定編集用
  const [editPlanDialogOpen, setEditPlanDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan | null>(null)
  const [editPlanForm, setEditPlanForm] = useState({
    name: '',
    nameJa: '',
    description: '',
    basePrice: 0,
    pricePerUser: 0,
    maxUsers: null as number | null,
    aiCreditsPerMonth: null as number | null,
    aiCreditPrice: 0,
    creditCarryoverMonths: 0,
    reportRetentionYears: 0,
    maxLearningPlans: null as number | null,
  })

  // プラン新規作成用
  const [createPlanDialogOpen, setCreatePlanDialogOpen] = useState(false)
  const [createPlanForm, setCreatePlanForm] = useState({
    planType: '',
    name: '',
    nameJa: '',
    description: '',
    basePrice: 0,
    pricePerUser: 0,
    maxUsers: null as number | null,
    aiCreditsPerMonth: null as number | null,
    aiCreditPrice: 0,
    creditCarryoverMonths: 0,
    reportRetentionYears: 1,
    maxLearningPlans: null as number | null,
  })

  // Data fetching
  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/plans', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setPlans(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err)
    }
  }, [token])

  const fetchSubscriptions = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/subscriptions', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setSubscriptions(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err)
    }
  }, [token])

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/invoices', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setInvoices(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err)
    }
  }, [token])

  const fetchCredits = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/credits/all', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setCredits(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch credits:', err)
    }
  }, [token])

  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/billing/scheduler/status', {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json()
      if (data.success) {
        setSchedulerStatus(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch scheduler status:', err)
    }
  }, [token])

  const fetchTokenUsageSummary = useCallback(async (month?: string) => {
    setTokenUsageLoading(true)
    try {
      const data = await billingService.getTokenUsageSummary(month)
      setTokenUsageSummary(data)
    } catch (err) {
      console.error('Failed to fetch token usage summary:', err)
    } finally {
      setTokenUsageLoading(false)
    }
  }, [])

  const fetchTenantTokenDetail = useCallback(async (tenantId: string, month?: string) => {
    setTenantDetailLoading(true)
    try {
      const data = await billingService.getTokenUsageByTenant(tenantId, { month })
      setSelectedTenantDetail(data)
      setTenantDetailDialogOpen(true)
    } catch (err) {
      console.error('Failed to fetch tenant token detail:', err)
      setSnackbar({
        open: true,
        message: 'テナント詳細の取得に失敗しました',
        severity: 'error',
      })
    } finally {
      setTenantDetailLoading(false)
    }
  }, [])

  const handleExportTokenUsage = useCallback(
    async (tenantId?: string) => {
      try {
        await billingService.exportTokenUsage({ month: selectedMonth, tenantId })
        setSnackbar({
          open: true,
          message: 'CSVエクスポートが完了しました',
          severity: 'success',
        })
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'CSVエクスポートに失敗しました',
          severity: 'error',
        })
      }
    },
    [selectedMonth]
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await Promise.all([
        fetchPlans(),
        fetchSubscriptions(),
        fetchInvoices(),
        fetchCredits(),
        fetchSchedulerStatus(),
        fetchTokenUsageSummary(selectedMonth),
      ])
    } catch (err) {
      setError('データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [
    fetchPlans,
    fetchSubscriptions,
    fetchInvoices,
    fetchCredits,
    fetchSchedulerStatus,
    fetchTokenUsageSummary,
    selectedMonth,
  ])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  // 月選択変更時にトークン使用量を再取得
  useEffect(() => {
    if (!loading) {
      fetchTokenUsageSummary(selectedMonth)
    }
  }, [selectedMonth]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleChangePlan = async () => {
    if (!selectedSubscription || !newPlanId) return

    setSubmitting(true)
    try {
      const response = await fetch(
        `/api/billing/subscriptions/${selectedSubscription.id}/change-plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            newPlanId,
            reason: planChangeReason,
          }),
        }
      )

      const data = await response.json()
      if (data.success) {
        setSnackbar({ open: true, message: 'プラン変更が完了しました', severity: 'success' })
        setChangePlanDialogOpen(false)
        fetchSubscriptions()
        fetchInvoices()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'プラン変更に失敗しました',
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/billing/invoices/${invoiceId}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({ open: true, message: '請求書を送信しました', severity: 'success' })
        fetchInvoices()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : '請求書送信に失敗しました',
        severity: 'error',
      })
    }
  }

  const handleMarkPaid = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/billing/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paidAt: new Date().toISOString() }),
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({ open: true, message: '支払済みにマークしました', severity: 'success' })
        fetchInvoices()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : '処理に失敗しました',
        severity: 'error',
      })
    }
  }

  const handleRunMonthlyBilling = async () => {
    setSubmitting(true)
    try {
      const response = await fetch('/api/billing/scheduler/run-monthly', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({
          open: true,
          message: `月次請求処理完了: ${data.data.invoicesGenerated}件生成`,
          severity: 'success',
        })
        fetchInvoices()
        fetchSchedulerStatus()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : '月次請求処理に失敗しました',
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleProcessCarryover = async () => {
    setSubmitting(true)
    try {
      const response = await fetch('/api/billing/scheduler/process-carryover', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({
          open: true,
          message: `クレジット繰越処理完了: ${data.data.processed}件`,
          severity: 'success',
        })
        fetchCredits()
        fetchSchedulerStatus()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'クレジット繰越処理に失敗しました',
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // プラン編集ダイアログを開く
  const handleOpenEditPlan = (plan: BillingPlan) => {
    setSelectedPlan(plan)
    setEditPlanForm({
      name: plan.name,
      nameJa: plan.nameJa,
      description: plan.description || '',
      basePrice: plan.basePrice,
      pricePerUser: plan.pricePerUser,
      maxUsers: plan.maxUsers,
      aiCreditsPerMonth: plan.aiCreditsPerMonth,
      aiCreditPrice: plan.aiCreditPrice,
      creditCarryoverMonths: plan.creditCarryoverMonths,
      reportRetentionYears: plan.reportRetentionYears,
      maxLearningPlans: plan.maxLearningPlans,
    })
    setEditPlanDialogOpen(true)
  }

  // プラン更新
  const handleUpdatePlan = async () => {
    if (!selectedPlan) return

    setSubmitting(true)
    try {
      const response = await fetch(`/api/billing/plans/${selectedPlan.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editPlanForm),
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({ open: true, message: 'プラン設定を更新しました', severity: 'success' })
        setEditPlanDialogOpen(false)
        fetchPlans()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'プラン更新に失敗しました',
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // プラン新規作成ダイアログを開く
  const handleOpenCreatePlan = () => {
    setCreatePlanForm({
      planType: '',
      name: '',
      nameJa: '',
      description: '',
      basePrice: 0,
      pricePerUser: 0,
      maxUsers: null,
      aiCreditsPerMonth: 100,
      aiCreditPrice: 0,
      creditCarryoverMonths: 0,
      reportRetentionYears: 1,
      maxLearningPlans: null,
    })
    setCreatePlanDialogOpen(true)
  }

  // プラン新規作成
  const handleCreatePlan = async () => {
    if (!createPlanForm.planType || !createPlanForm.name || !createPlanForm.nameJa) {
      setSnackbar({
        open: true,
        message: 'プランタイプ、プラン名（英語）、プラン名（日本語）は必須です',
        severity: 'error',
      })
      return
    }

    setSubmitting(true)
    try {
      const response = await fetch('/api/billing/plans', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createPlanForm),
      })

      const data = await response.json()
      if (data.success) {
        setSnackbar({ open: true, message: '新しいプランを作成しました', severity: 'success' })
        setCreatePlanDialogOpen(false)
        fetchPlans()
      } else {
        throw new Error(data.error)
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'プラン作成に失敗しました',
        severity: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  // Helper functions
  const getStatusChip = (status: string) => {
    const statusMap: Record<
      string,
      { label: string; color: 'primary' | 'secondary' | 'error' | 'default' }
    > = {
      ACTIVE: { label: '有効', color: 'primary' },
      CANCELLED: { label: 'キャンセル', color: 'error' },
      SUSPENDED: { label: '停止中', color: 'secondary' },
      DRAFT: { label: '下書き', color: 'default' },
      PENDING: { label: '未払い', color: 'secondary' },
      SENT: { label: '送信済み', color: 'default' },
      PAID: { label: '支払済', color: 'primary' },
      OVERDUE: { label: '延滞', color: 'error' },
    }
    const config = statusMap[status] || { label: status, color: 'default' as const }
    return <Chip size="small" label={config.label} color={config.color} />
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom>
        課金管理
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          テナントの契約・請求書・クレジットを管理します
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAllData}>
          更新
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <BusinessIcon color="primary" />
              <Typography variant="h6">{subscriptions.length}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              契約テナント
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ReceiptIcon color="secondary" />
              <Typography variant="h6">
                {invoices.filter((i) => i.status === 'PENDING').length}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              未払い請求書
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AttachMoneyIcon color="primary" />
              <Typography variant="h6">
                {formatCurrency(
                  invoices
                    .filter((i) => i.status === 'PAID')
                    .reduce((sum, i) => sum + i.totalAmount, 0)
                )}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              今月の売上
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleIcon color={schedulerStatus?.isRunning ? 'primary' : 'secondary'} />
              <Typography variant="h6">{schedulerStatus?.isRunning ? '稼働中' : '停止'}</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              スケジューラ
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="プラン設定" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="契約管理" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="請求書" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="クレジット" icon={<CreditCardIcon />} iconPosition="start" />
          <Tab label="スケジューラ" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="トークン使用量" icon={<TokenIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab: Plan Settings */}
      <TabPanel value={tabValue} index={0}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Alert severity="info" sx={{ flex: 1 }}>
              各プランのパラメータを編集できます。変更は即座に反映され、新規契約および既存契約の次回請求から適用されます。
            </Alert>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreatePlan}
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            >
              新規プラン作成
            </Button>
          </Stack>

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            sx={{ flexWrap: 'wrap', pt: 2, alignItems: 'stretch' }}
          >
            {plans.map((plan) => (
              <Card
                key={plan.id}
                sx={{
                  flex: { xs: '1 1 100%', lg: '1 1 0' },
                  minWidth: { xs: 'auto', lg: 220 },
                  position: 'relative',
                  overflow: 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {plan.planType === 'BASIC' && (
                  <Chip
                    label="おすすめ"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                    }}
                  />
                )}
                {plan.planType === 'DEMO' && (
                  <Chip
                    label="SUPER_ADMIN専用"
                    color="secondary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                    }}
                  />
                )}
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Box textAlign="center">
                      <Typography variant="h5" fontWeight="bold">
                        {plan.nameJa}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plan.planType === 'FREE' && '小規模チームのお試しに'}
                        {plan.planType === 'BASIC' && '成長企業に最適'}
                        {plan.planType === 'CUSTOM' && '大企業向けソリューション'}
                        {plan.planType === 'DEMO' && 'デモ・検証用（全機能無制限）'}
                      </Typography>
                    </Box>

                    <Box textAlign="center">
                      {plan.planType === 'CUSTOM' ? (
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          お見積り
                        </Typography>
                      ) : plan.planType === 'DEMO' ? (
                        <Typography variant="h4" color="secondary.main" fontWeight="bold">
                          無料（デモ専用）
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="h3" color="primary" fontWeight="bold">
                            {formatCurrency(plan.basePrice)}
                            <Typography component="span" variant="body1">
                              /月
                            </Typography>
                          </Typography>
                          {plan.pricePerUser > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              + {formatCurrency(plan.pricePerUser)}/ユーザー
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>

                    <Divider />

                    <Stack spacing={1.5} sx={{ flex: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          ユーザー{plan.maxUsers ? `${plan.maxUsers}名まで` : '数無制限'}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          AI生成クレジット{' '}
                          {plan.aiCreditsPerMonth === null
                            ? '無制限'
                            : `${plan.aiCreditsPerMonth}回/月`}
                        </Typography>
                      </Box>
                      {plan.aiCreditPrice > 0 && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                          <Typography variant="body2">
                            追加クレジット {formatCurrency(plan.aiCreditPrice)}/回
                          </Typography>
                        </Box>
                      )}
                      {plan.creditCarryoverMonths > 0 && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                          <Typography variant="body2">
                            クレジット
                            {plan.creditCarryoverMonths === 12
                              ? '1年間'
                              : `${plan.creditCarryoverMonths}ヶ月`}
                            繰越可能
                          </Typography>
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          レポート保存期間 {plan.reportRetentionYears}年間
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleIcon color="primary" fontSize="small" />
                        <Typography variant="body2">
                          学習計画
                          {plan.maxLearningPlans ? `${plan.maxLearningPlans}件まで` : '無制限'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditPlan(plan)}
                      >
                        編集
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Stack>
      </TabPanel>

      {/* Tab: Subscriptions */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>テナント</TableCell>
                <TableCell>プラン</TableCell>
                <TableCell align="right">ユーザー数</TableCell>
                <TableCell align="right">月額料金</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>開始日</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscriptions.map((sub) => {
                const monthlyAmount =
                  sub.billingPlan.basePrice + sub.billingPlan.pricePerUser * sub.activeUserCount
                return (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {sub.tenant.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.tenant.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={sub.billingPlan.nameJa}
                        color={sub.billingPlan.planType === 'FREE' ? 'default' : 'primary'}
                      />
                    </TableCell>
                    <TableCell align="right">{sub.activeUserCount}名</TableCell>
                    <TableCell align="right">{formatCurrency(monthlyAmount)}</TableCell>
                    <TableCell>{getStatusChip(sub.status)}</TableCell>
                    <TableCell>
                      {format(new Date(sub.startDate), 'yyyy/MM/dd', { locale })}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedSubscription(sub)
                          setNewPlanId('')
                          setPlanChangeReason('')
                          setChangePlanDialogOpen(true)
                        }}
                      >
                        プラン変更
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab: Invoices */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>請求書番号</TableCell>
                <TableCell>テナント</TableCell>
                <TableCell>請求期間</TableCell>
                <TableCell align="right">合計金額</TableCell>
                <TableCell>ステータス</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{invoice.tenant.name}</TableCell>
                  <TableCell>
                    {format(new Date(invoice.billingPeriodStart), 'yyyy/MM/dd', { locale })} -{' '}
                    {format(new Date(invoice.billingPeriodEnd), 'yyyy/MM/dd', { locale })}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(invoice.totalAmount)}</TableCell>
                  <TableCell>{getStatusChip(invoice.status)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="詳細">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedInvoice(invoice)
                            setInvoiceDialogOpen(true)
                          }}
                        >
                          <ReceiptIcon />
                        </IconButton>
                      </Tooltip>
                      {invoice.status === 'PENDING' && (
                        <>
                          <Tooltip title="送信">
                            <IconButton size="small" onClick={() => handleSendInvoice(invoice.id)}>
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="支払済みにする">
                            <IconButton size="small" onClick={() => handleMarkPaid(invoice.id)}>
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {invoice.status === 'SENT' && (
                        <Tooltip title="支払済みにする">
                          <IconButton size="small" onClick={() => handleMarkPaid(invoice.id)}>
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">請求書がありません</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab: Credits */}
      <TabPanel value={tabValue} index={3}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>テナント</TableCell>
                <TableCell>プラン</TableCell>
                <TableCell align="right">月間付与</TableCell>
                <TableCell align="right">繰越</TableCell>
                <TableCell align="right">使用済</TableCell>
                <TableCell align="right">残高</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {credits.map((credit) => (
                <TableRow key={credit.tenant.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {credit.tenant.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{credit.plan.nameJa}</TableCell>
                  <TableCell align="right">{credit.balance?.creditsGranted ?? '-'}</TableCell>
                  <TableCell align="right">{credit.balance?.creditsCarriedOver ?? '-'}</TableCell>
                  <TableCell align="right">{credit.balance?.creditsUsed ?? '-'}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={credit.balance?.creditsRemaining ?? 0}
                      color={
                        (credit.balance?.creditsRemaining ?? 0) > 0
                          ? 'primary'
                          : (credit.balance?.creditsRemaining ?? 0) === 0
                            ? 'default'
                            : 'secondary'
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab: Scheduler */}
      <TabPanel value={tabValue} index={4}>
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                スケジューラステータス
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>ステータス:</Typography>
                  <Chip
                    label={schedulerStatus?.isRunning ? '稼働中' : '停止'}
                    color={schedulerStatus?.isRunning ? 'primary' : 'secondary'}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>次回実行:</Typography>
                  <Typography>
                    {schedulerStatus?.nextExecution
                      ? format(new Date(schedulerStatus.nextExecution), 'yyyy/MM/dd HH:mm', {
                          locale,
                        })
                      : '-'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>最終実行:</Typography>
                  <Typography>
                    {schedulerStatus?.lastExecution
                      ? format(new Date(schedulerStatus.lastExecution), 'yyyy/MM/dd HH:mm', {
                          locale,
                        })
                      : '未実行'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                手動実行
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleRunMonthlyBilling}
                  disabled={submitting}
                >
                  月次請求処理を実行
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleProcessCarryover}
                  disabled={submitting}
                >
                  クレジット繰越処理を実行
                </Button>
              </Stack>
              <Alert severity="info" sx={{ mt: 2 }}>
                月次請求処理は通常、毎月1日に自動実行されます。手動実行は緊急時のみ使用してください。
              </Alert>
            </CardContent>
          </Card>
        </Stack>
      </TabPanel>

      {/* Tab: Token Usage */}
      <TabPanel value={tabValue} index={5}>
        <Stack spacing={3}>
          {/* Header with month selector and export button */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>期間</InputLabel>
              <Select
                value={selectedMonth}
                label="期間"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {billingService.getMonthOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExportTokenUsage()}
              disabled={tokenUsageLoading}
            >
              CSVエクスポート
            </Button>
          </Stack>

          {tokenUsageLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : tokenUsageSummary ? (
            <>
              {/* Summary Cards */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TokenIcon color="primary" />
                      <Typography variant="h5" fontWeight="bold">
                        {billingService.formatTokenCount(tokenUsageSummary.summary.totalTokens)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      今月のトークン使用量
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CreditCardIcon color="primary" />
                      <Typography variant="h5" fontWeight="bold">
                        {tokenUsageSummary.summary.totalCredits}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      使用クレジット数
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <InfoIcon color="secondary" />
                      <Typography variant="h5" fontWeight="bold">
                        {tokenUsageSummary.summary.totalCount}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      API呼び出し回数
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              {/* Charts Row */}
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
                {/* Monthly Trend Chart */}
                <Card sx={{ flex: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      月別トークン使用量推移
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={tokenUsageSummary.monthlyTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            tickFormatter={(value) => {
                              const [, m] = value.split('-')
                              return `${parseInt(m)}月`
                            }}
                          />
                          <YAxis
                            tickFormatter={(value) => billingService.formatTokenCount(value)}
                          />
                          <RechartsTooltip
                            formatter={(value) => [
                              billingService.formatTokenCount(Number(value) || 0),
                              'トークン数',
                            ]}
                            labelFormatter={(label) => {
                              const [y, m] = String(label).split('-')
                              return `${y}年${parseInt(m)}月`
                            }}
                          />
                          <Bar dataKey="tokens" fill="#1F3A5F" name="トークン数" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>

                {/* Usage Type Pie Chart */}
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      使用タイプ別内訳
                    </Typography>
                    <Box sx={{ height: 300 }}>
                      {tokenUsageSummary.byType.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={tokenUsageSummary.byType.map((item) => ({
                                name: billingService.getUsageTypeLabel(item.type),
                                value: item.tokens,
                              }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) =>
                                `${name} ${((percent || 0) * 100).toFixed(0)}%`
                              }
                            >
                              {tokenUsageSummary.byType.map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={['#1F3A5F', '#3FB984', '#2C6EBE', '#EF4444'][index % 4]}
                                />
                              ))}
                            </Pie>
                            <Legend />
                            <RechartsTooltip
                              formatter={(value) => [
                                billingService.formatTokenCount(Number(value) || 0),
                                'トークン数',
                              ]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                        >
                          <Typography color="text.secondary">データがありません</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>

              {/* Tenant Usage Table */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    テナント別使用量
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>テナント</TableCell>
                          <TableCell align="right">今月のトークン</TableCell>
                          <TableCell align="right">今月のクレジット</TableCell>
                          <TableCell align="right">累計トークン</TableCell>
                          <TableCell align="right">API呼び出し</TableCell>
                          <TableCell>操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tokenUsageSummary.byTenant.map((tenant) => (
                          <TableRow key={tenant.tenantId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {tenant.tenantName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {tenant.tenantSlug}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {billingService.formatTokenCount(tenant.monthlyTokens)}
                            </TableCell>
                            <TableCell align="right">{tenant.monthlyCredits}</TableCell>
                            <TableCell align="right">
                              {billingService.formatTokenCount(tenant.totalTokens)}
                            </TableCell>
                            <TableCell align="right">{tenant.monthlyCount}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    fetchTenantTokenDetail(tenant.tenantId, selectedMonth)
                                  }
                                  disabled={tenantDetailLoading}
                                >
                                  詳細
                                </Button>
                                <Tooltip title="テナント別CSV">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleExportTokenUsage(tenant.tenantId)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                        {tokenUsageSummary.byTenant.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography color="text.secondary">データがありません</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          ) : (
            <Alert severity="info">トークン使用量データを取得中...</Alert>
          )}
        </Stack>
      </TabPanel>

      {/* Change Plan Dialog */}
      <Dialog
        open={changePlanDialogOpen}
        onClose={() => setChangePlanDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          プラン変更
          <IconButton
            onClick={() => setChangePlanDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography>
              テナント: <strong>{selectedSubscription?.tenant.name}</strong>
            </Typography>
            <Typography>
              現在のプラン: <strong>{selectedSubscription?.billingPlan.nameJa}</strong>
            </Typography>
            <FormControl fullWidth>
              <InputLabel>新しいプラン</InputLabel>
              <Select
                value={newPlanId}
                onChange={(e) => setNewPlanId(e.target.value)}
                label="新しいプラン"
              >
                {plans
                  .filter((p) => p.id !== selectedSubscription?.billingPlanId)
                  .map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.nameJa} - {formatCurrency(plan.basePrice)}/月
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="変更理由（任意）"
              multiline
              rows={2}
              value={planChangeReason}
              onChange={(e) => setPlanChangeReason(e.target.value)}
            />
            <Alert severity="warning">
              プラン変更は即時適用され、日割り計算による調整請求書が発行されます。
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePlanDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleChangePlan}
            disabled={!newPlanId || submitting}
          >
            {submitting ? <CircularProgress size={24} /> : '変更する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          請求書詳細
          <IconButton
            onClick={() => setInvoiceDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">{selectedInvoice.invoiceNumber}</Typography>
                {getStatusChip(selectedInvoice.status)}
              </Box>
              <Divider />
              <Typography>
                <strong>テナント:</strong> {selectedInvoice.tenant.name}
              </Typography>
              <Typography>
                <strong>請求期間:</strong>{' '}
                {format(new Date(selectedInvoice.billingPeriodStart), 'yyyy/MM/dd', { locale })} -{' '}
                {format(new Date(selectedInvoice.billingPeriodEnd), 'yyyy/MM/dd', { locale })}
              </Typography>
              <Divider />
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>基本料金</TableCell>
                    <TableCell align="right">
                      {formatCurrency(selectedInvoice.baseAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>ユーザー利用料（{selectedInvoice.activeUserCount}名）</TableCell>
                    <TableCell align="right">
                      {formatCurrency(selectedInvoice.userAmount)}
                    </TableCell>
                  </TableRow>
                  {selectedInvoice.aiCreditAmount > 0 && (
                    <TableRow>
                      <TableCell>追加AIクレジット（{selectedInvoice.aiCreditsExtra}回）</TableCell>
                      <TableCell align="right">
                        {formatCurrency(selectedInvoice.aiCreditAmount)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <strong>小計</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(selectedInvoice.subtotal)}</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>消費税（{selectedInvoice.taxRate}%）</TableCell>
                    <TableCell align="right">{formatCurrency(selectedInvoice.taxAmount)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">合計</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6">
                        {formatCurrency(selectedInvoice.totalAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog
        open={editPlanDialogOpen}
        onClose={() => setEditPlanDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          プラン設定編集
          <IconButton
            onClick={() => setEditPlanDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedPlan.nameJa}（{selectedPlan.planType}）
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  プランタイプは変更できません。パラメータのみ編集可能です。
                </Typography>
              </Box>

              <Divider />

              {/* 基本情報 */}
              <Typography variant="subtitle1" fontWeight="bold">
                基本情報
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="プラン名（英語）"
                  fullWidth
                  value={editPlanForm.name}
                  onChange={(e) => setEditPlanForm({ ...editPlanForm, name: e.target.value })}
                />
                <TextField
                  label="プラン名（日本語）"
                  fullWidth
                  value={editPlanForm.nameJa}
                  onChange={(e) => setEditPlanForm({ ...editPlanForm, nameJa: e.target.value })}
                />
              </Stack>
              <TextField
                label="説明"
                multiline
                rows={2}
                fullWidth
                value={editPlanForm.description}
                onChange={(e) => setEditPlanForm({ ...editPlanForm, description: e.target.value })}
              />

              <Divider />

              {/* 料金設定 */}
              <Typography variant="subtitle1" fontWeight="bold">
                料金設定
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="基本月額料金（円）"
                  type="number"
                  fullWidth
                  value={editPlanForm.basePrice}
                  onChange={(e) =>
                    setEditPlanForm({ ...editPlanForm, basePrice: parseInt(e.target.value) || 0 })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
                <TextField
                  label="ユーザー単価（円/人）"
                  type="number"
                  fullWidth
                  value={editPlanForm.pricePerUser}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      pricePerUser: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Stack>

              <Divider />

              {/* ユーザー数制限 */}
              <Typography variant="subtitle1" fontWeight="bold">
                ユーザー数制限
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  label="最大ユーザー数"
                  type="number"
                  fullWidth
                  value={editPlanForm.maxUsers ?? ''}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      maxUsers: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="空欄で無制限"
                />
                <Button
                  variant="outlined"
                  onClick={() => setEditPlanForm({ ...editPlanForm, maxUsers: null })}
                  sx={{ whiteSpace: 'nowrap', height: 56 }}
                >
                  無制限にする
                </Button>
              </Stack>

              <Divider />

              {/* AIクレジット設定 */}
              <Typography variant="subtitle1" fontWeight="bold">
                AIクレジット設定
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  label="月間AI生成クレジット"
                  type="number"
                  fullWidth
                  value={editPlanForm.aiCreditsPerMonth ?? ''}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      aiCreditsPerMonth: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText="空欄で無制限"
                  disabled={editPlanForm.aiCreditsPerMonth === null}
                />
                <Button
                  variant="outlined"
                  onClick={() => setEditPlanForm({ ...editPlanForm, aiCreditsPerMonth: null })}
                  sx={{ whiteSpace: 'nowrap', height: 56 }}
                >
                  無制限にする
                </Button>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="追加クレジット単価（円/回）"
                  type="number"
                  fullWidth
                  value={editPlanForm.aiCreditPrice}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      aiCreditPrice: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0 } }}
                />
                <TextField
                  label="クレジット繰越月数"
                  type="number"
                  fullWidth
                  value={editPlanForm.creditCarryoverMonths}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      creditCarryoverMonths: parseInt(e.target.value) || 0,
                    })
                  }
                  InputProps={{ inputProps: { min: 0, max: 24 } }}
                  helperText="0: 繰越なし、12: 1年間繰越可能"
                />
              </Stack>

              <Divider />

              {/* その他の制限 */}
              <Typography variant="subtitle1" fontWeight="bold">
                その他の制限
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="レポート保存期間（年）"
                  type="number"
                  fullWidth
                  value={editPlanForm.reportRetentionYears}
                  onChange={(e) =>
                    setEditPlanForm({
                      ...editPlanForm,
                      reportRetentionYears: parseInt(e.target.value) || 1,
                    })
                  }
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                />
                <Stack direction="row" spacing={2} sx={{ flex: 1 }} alignItems="center">
                  <TextField
                    label="最大学習計画数"
                    type="number"
                    fullWidth
                    value={editPlanForm.maxLearningPlans ?? ''}
                    onChange={(e) =>
                      setEditPlanForm({
                        ...editPlanForm,
                        maxLearningPlans: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    InputProps={{ inputProps: { min: 1 } }}
                    helperText="空欄で無制限"
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setEditPlanForm({ ...editPlanForm, maxLearningPlans: null })}
                    sx={{ whiteSpace: 'nowrap', height: 56 }}
                  >
                    無制限
                  </Button>
                </Stack>
              </Stack>

              <Alert severity="warning" sx={{ mt: 2 }}>
                設定変更は保存後すぐに反映されます。既存の契約テナントにも次回請求から新しい設定が適用されます。
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPlanDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleUpdatePlan} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog
        open={createPlanDialogOpen}
        onClose={() => setCreatePlanDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          新規プラン作成
          <IconButton
            onClick={() => setCreatePlanDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Alert severity="info">
              新しい課金プランを作成します。プランタイプは作成後に変更できません。
            </Alert>

            <Divider />

            {/* プランタイプと基本情報 */}
            <Typography variant="subtitle1" fontWeight="bold">
              プランタイプと基本情報
            </Typography>
            <TextField
              label="プランタイプ（識別子）"
              fullWidth
              required
              value={createPlanForm.planType}
              onChange={(e) =>
                setCreatePlanForm({ ...createPlanForm, planType: e.target.value.toUpperCase() })
              }
              helperText="例: PREMIUM, ENTERPRISE, STARTER など（英大文字推奨）"
              placeholder="PREMIUM"
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="プラン名（英語）"
                fullWidth
                required
                value={createPlanForm.name}
                onChange={(e) => setCreatePlanForm({ ...createPlanForm, name: e.target.value })}
                placeholder="Premium Plan"
              />
              <TextField
                label="プラン名（日本語）"
                fullWidth
                required
                value={createPlanForm.nameJa}
                onChange={(e) => setCreatePlanForm({ ...createPlanForm, nameJa: e.target.value })}
                placeholder="プレミアムプラン"
              />
            </Stack>
            <TextField
              label="説明"
              multiline
              rows={2}
              fullWidth
              value={createPlanForm.description}
              onChange={(e) =>
                setCreatePlanForm({ ...createPlanForm, description: e.target.value })
              }
            />

            <Divider />

            {/* 料金設定 */}
            <Typography variant="subtitle1" fontWeight="bold">
              料金設定
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="基本月額料金（円）"
                type="number"
                fullWidth
                value={createPlanForm.basePrice}
                onChange={(e) =>
                  setCreatePlanForm({ ...createPlanForm, basePrice: parseInt(e.target.value) || 0 })
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label="ユーザー単価（円/人）"
                type="number"
                fullWidth
                value={createPlanForm.pricePerUser}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    pricePerUser: parseInt(e.target.value) || 0,
                  })
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Stack>

            <Divider />

            {/* ユーザー数制限 */}
            <Typography variant="subtitle1" fontWeight="bold">
              ユーザー数制限
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="最大ユーザー数"
                type="number"
                fullWidth
                value={createPlanForm.maxUsers ?? ''}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    maxUsers: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                InputProps={{ inputProps: { min: 1 } }}
                helperText="空欄で無制限"
              />
              <Button
                variant="outlined"
                onClick={() => setCreatePlanForm({ ...createPlanForm, maxUsers: null })}
                sx={{ whiteSpace: 'nowrap', height: 56 }}
              >
                無制限にする
              </Button>
            </Stack>

            <Divider />

            {/* AIクレジット設定 */}
            <Typography variant="subtitle1" fontWeight="bold">
              AIクレジット設定
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                label="月間AI生成クレジット"
                type="number"
                fullWidth
                value={createPlanForm.aiCreditsPerMonth ?? ''}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    aiCreditsPerMonth: e.target.value ? parseInt(e.target.value) : null,
                  })
                }
                InputProps={{ inputProps: { min: 0 } }}
                helperText="空欄で無制限"
                disabled={createPlanForm.aiCreditsPerMonth === null}
              />
              <Button
                variant="outlined"
                onClick={() => setCreatePlanForm({ ...createPlanForm, aiCreditsPerMonth: null })}
                sx={{ whiteSpace: 'nowrap', height: 56 }}
              >
                無制限にする
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="追加クレジット単価（円/回）"
                type="number"
                fullWidth
                value={createPlanForm.aiCreditPrice}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    aiCreditPrice: parseInt(e.target.value) || 0,
                  })
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label="クレジット繰越月数"
                type="number"
                fullWidth
                value={createPlanForm.creditCarryoverMonths}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    creditCarryoverMonths: parseInt(e.target.value) || 0,
                  })
                }
                InputProps={{ inputProps: { min: 0, max: 24 } }}
                helperText="0: 繰越なし、12: 1年間繰越可能"
              />
            </Stack>

            <Divider />

            {/* その他の制限 */}
            <Typography variant="subtitle1" fontWeight="bold">
              その他の制限
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="レポート保存期間（年）"
                type="number"
                fullWidth
                value={createPlanForm.reportRetentionYears}
                onChange={(e) =>
                  setCreatePlanForm({
                    ...createPlanForm,
                    reportRetentionYears: parseInt(e.target.value) || 1,
                  })
                }
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
              <Stack direction="row" spacing={2} sx={{ flex: 1 }} alignItems="center">
                <TextField
                  label="最大学習計画数"
                  type="number"
                  fullWidth
                  value={createPlanForm.maxLearningPlans ?? ''}
                  onChange={(e) =>
                    setCreatePlanForm({
                      ...createPlanForm,
                      maxLearningPlans: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="空欄で無制限"
                />
                <Button
                  variant="outlined"
                  onClick={() => setCreatePlanForm({ ...createPlanForm, maxLearningPlans: null })}
                  sx={{ whiteSpace: 'nowrap', height: 56 }}
                >
                  無制限
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePlanDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleCreatePlan}
            disabled={
              submitting ||
              !createPlanForm.planType ||
              !createPlanForm.name ||
              !createPlanForm.nameJa
            }
          >
            {submitting ? <CircularProgress size={24} /> : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Token Detail Dialog */}
      <Dialog
        open={tenantDetailDialogOpen}
        onClose={() => setTenantDetailDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          テナント別トークン使用量詳細
          <IconButton
            onClick={() => setTenantDetailDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {tenantDetailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : selectedTenantDetail ? (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Tenant Info */}
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedTenantDetail.tenant.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedTenantDetail.tenant.slug}
                </Typography>
              </Box>

              <Divider />

              {/* Charts Row */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                {/* Monthly Trend */}
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      月別推移
                    </Typography>
                    <Box sx={{ height: 250 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={selectedTenantDetail.monthlyTrend}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="month"
                            tickFormatter={(value) => {
                              const [, m] = value.split('-')
                              return `${parseInt(m)}月`
                            }}
                          />
                          <YAxis
                            tickFormatter={(value) => billingService.formatTokenCount(value)}
                          />
                          <RechartsTooltip
                            formatter={(value) => [
                              billingService.formatTokenCount(Number(value) || 0),
                              'トークン数',
                            ]}
                          />
                          <Bar dataKey="tokens" fill="#1F3A5F" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>

                {/* Type Breakdown */}
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      タイプ別内訳
                    </Typography>
                    <Box sx={{ height: 250 }}>
                      {selectedTenantDetail.byType.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={selectedTenantDetail.byType.map((item) => ({
                                name: billingService.getUsageTypeLabel(item.type),
                                value: item.tokens,
                              }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={5}
                              dataKey="value"
                            >
                              {selectedTenantDetail.byType.map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={['#1F3A5F', '#3FB984', '#2C6EBE'][index % 3]}
                                />
                              ))}
                            </Pie>
                            <Legend />
                            <RechartsTooltip
                              formatter={(value) => [
                                billingService.formatTokenCount(Number(value) || 0),
                                'トークン数',
                              ]}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          height="100%"
                        >
                          <Typography color="text.secondary">データがありません</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>

              {/* Usage History Table */}
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    使用履歴（最新{selectedTenantDetail.usages.length}件）
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>日時</TableCell>
                          <TableCell>タイプ</TableCell>
                          <TableCell align="right">トークン数</TableCell>
                          <TableCell align="right">クレジット</TableCell>
                          <TableCell>説明</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTenantDetail.usages.map((usage) => (
                          <TableRow key={usage.id}>
                            <TableCell>
                              {format(new Date(usage.usedAt), 'MM/dd HH:mm', { locale })}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={billingService.getUsageTypeLabel(usage.usageType)}
                                color={
                                  usage.usageType === 'TEST_GENERATION' ? 'primary' : 'secondary'
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {usage.tokenCount
                                ? billingService.formatTokenCount(usage.tokenCount)
                                : '-'}
                            </TableCell>
                            <TableCell align="right">{usage.creditsUsed}</TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                sx={{
                                  maxWidth: 200,
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {usage.description || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {selectedTenantDetail.usages.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography color="text.secondary">使用履歴がありません</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {selectedTenantDetail.pagination.totalPages > 1 && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      全{selectedTenantDetail.pagination.total}件中
                      {selectedTenantDetail.usages.length}件を表示
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTenantDetailDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default BillingManagementPage
